import React from 'react';
import { makeStyles, Grid } from '@material-ui/core';
// @ts-ignore
import ReactSpeedometer from 'react-d3-speedometer';
import { PolicyRiskScoreNamed } from 'store/modules/Charts/types';
import { RiskGroupType } from 'store/modules/Firm/types';
import { useTheme } from '@material-ui/styles';
import {
  existingVsRecommendedIndex,
  gradientColors
} from 'components/Settings/Palette/utils';

const useStyles = makeStyles({
  chartBox: {
    height: '100%',
    padding: '2rem 2rem 0.5rem'
  },
  container: {
    position: 'relative',
    flex: 1,
    maxWidth: '500px',
    width: '100%',
    '&  div:first-child': {
      zIndex: 1000
    },
    '& div': {
      position: 'absolute',
      left: '50%',
      transform: 'translate(-50%,0)'
    },
    '& .percent-text': {
      color: 'yellow'
    }
  },
  legend: {
    width: '100%',
    marginTop: '1rem'
  },
  legendBox: {
    width: '1rem',
    height: '1rem',
    marginRight: '1rem',
    borderRadius: '50%'
  }
});

const RiskGaugeChart = ({
  data,
  hasExistingPolicies = true,
  riskGroup,
  footer
}: {
  data: Array<PolicyRiskScoreNamed>;
  hasExistingPolicies?: boolean;
  riskGroup: RiskGroupType;
  footer?: string;
}) => {
  const theme = useTheme() as any;
  const classes = useStyles();
  if (!data) return null;
  const existingData = data.find(
    (risk: PolicyRiskScoreNamed) => risk.name === 'Existing'
  );
  const recommendedData = data.find(
    (risk: PolicyRiskScoreNamed) => risk.name === 'Recommended'
  );

  const existingScore = existingData ? existingData.risk_score : 0;
  const recommendedScore = recommendedData ? recommendedData.risk_score : 0;

  return (
    <Grid
      item
      xs={12}
      className={classes.chartBox}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      <div className={classes.container}>
        {hasExistingPolicies && (
          <ReactSpeedometer // EXISTING
            needleHeightRatio={0.5}
            fluidWidth
            maxSegmentLabels={0}
            segments={1}
            minValue={1}
            needleTransitionDuration={0}
            maxValue={riskGroup}
            value={existingScore}
            segmentColors={['transparent']}
            needleColor={
              existingScore === 0
                ? 'transparent'
                : theme.palette.baseColors.charts['comparisons'][
                    existingVsRecommendedIndex('existing')
                  ]
            }
            currentValueText=""
            paddingHorizontal={2}
            paddingVertical={2}
          />
        )}
        <ReactSpeedometer // RECOMMENDED
          needleHeightRatio={0.8}
          fluidWidth
          needleColor={
            recommendedScore === 0
              ? 'transparent'
              : theme.palette.baseColors.charts['comparisons'][
                  existingVsRecommendedIndex('recommended')
                ]
          }
          segments={riskGroup}
          maxValue={riskGroup}
          minValue={1}
          needleTransitionDuration={0}
          value={recommendedScore}
          segmentColors={gradientColors(theme)}
          currentValueText=""
          paddingHorizontal={2}
          paddingVertical={2}
        />
      </div>
      <div className={classes.legend}>
        {hasExistingPolicies && (
          <div style={{ display: 'flex', marginBottom: '0.2rem' }}>
            <div
              className={classes.legendBox}
              style={{
                backgroundColor:
                  theme.palette.baseColors.charts['comparisons'][
                    existingVsRecommendedIndex('existing')
                  ]
              }}
            />
            <div>Existing: {existingScore}</div>
          </div>
        )}
        <div style={{ display: 'flex' }}>
          <div
            className={classes.legendBox}
            style={{
              backgroundColor:
                theme.palette.baseColors.charts['comparisons'][
                  existingVsRecommendedIndex('recommended')
                ]
            }}
          />
          <div>Recommended: {recommendedScore}</div>
        </div>
      </div>
      {footer && (
        <span style={{ alignSelf: 'flex-start', marginTop: '1rem' }}>
          {footer}
        </span>
      )}
    </Grid>
  );
};

export default RiskGaugeChart;
